import type { ApolloError } from 'apollo-client';
import { useQuery } from 'react-apollo';

import { isUnauthorizedError } from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';
import { usePageSpaceKey } from '@confluence/page-context';
import { getSuperAdminClaim } from '@confluence/super-admin-claim';
import { fg } from '@confluence/feature-gating';

import type {
	SpaceRestrictionCheckQueryExperimental as TData,
	SpaceRestrictionCheckQueryExperimentalVariables as TVariables,
} from './__types__/SpaceRestrictionCheckQueryExperimental';
import { SpaceRestrictionCheckQueryExperimental } from './SpaceRestrictionCheckQueryExperimental.experimentalgraphql';
import type { SpaceRestrictionCheckQuery as SpaceRestrictionCheckQueryType } from './__types__/SpaceRestrictionCheckQuery';
import { SpaceRestrictionCheckQuery } from './SpaceRestrictionCheckQuery.graphql';

type SpaceRestrictionCheckProps = {
	asSuperAdmin?: boolean;
	children: (result: {
		error: ApolloError | undefined;
		isSpaceNotFound: boolean | undefined;
		isSpaceRestricted: boolean | undefined;
		loading: boolean;
	}) => JSX.Element;
	spaceKey: string;
};

export function SpaceRestrictionCheck({
	asSuperAdmin,
	children,
	spaceKey,
}: SpaceRestrictionCheckProps) {
	const superAdminClaim = getSuperAdminClaim();
	const [stateSpaceKey] = usePageSpaceKey();

	const {
		data: experimentalData,
		error: experimentalError,
		loading: experimentalLoading,
	} = useQuery<TData, TVariables>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		SpaceRestrictionCheckQueryExperimental,
		{
			skip: fg('confluence_frontend_spacerestrictioncheckquery'),
			context: asSuperAdmin == undefined ? undefined : { asSuperAdmin },
			fetchPolicy: superAdminClaim ? 'no-cache' : undefined,
			variables: {
				// XXX Though SpaceRestrictionCheckProps defines spaceKey as required,
				// SpaceBaseComponent is not written in TypeScript and it may provide
				// undefined as it is reading spaceKey from redux. While it would result
				// in a better separation of concerns to have a fix in SpaceBaseComponent,
				// such a fix would be riskier.
				spaceKey: spaceKey || stateSpaceKey!,
			},
		},
	);

	const {
		data: newData,
		error: newError,
		loading: newLoading,
	} = useQuery<SpaceRestrictionCheckQueryType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations
		SpaceRestrictionCheckQuery,
		{
			skip: !fg('confluence_frontend_spacerestrictioncheckquery'),
			context: asSuperAdmin == undefined ? undefined : { asSuperAdmin },
			fetchPolicy: superAdminClaim ? 'no-cache' : 'network-only',
			errorPolicy: 'all',
			onError: (error) => {
				if (isSpaceNotFoundError(error) || isSpaceRestrictedError(error)) {
					markErrorAsHandled(error);
				}
			},
			variables: {
				// XXX Though SpaceRestrictionCheckProps defines spaceKey as required,
				// SpaceBaseComponent is not written in TypeScript and it may provide
				// undefined as it is reading spaceKey from redux. While it would result
				// in a better separation of concerns to have a fix in SpaceBaseComponent,
				// such a fix would be riskier.
				spaceKey: spaceKey || stateSpaceKey!,
			},
		},
	);

	const error = experimentalError || newError;
	const data = experimentalData || newData;
	const loading = experimentalLoading || newLoading;

	let isErrorHandled: boolean | undefined;
	let isSpaceNotFound: boolean | undefined;
	let isSpaceRestricted: boolean | undefined;

	if (error) {
		isSpaceNotFound = isSpaceNotFoundError(error);
		isSpaceRestricted = isSpaceRestrictedError(error);
		if (isSpaceNotFound || isSpaceRestricted) {
			if (!fg('confluence_frontend_spacerestrictioncheckquery')) {
				markErrorAsHandled(error);
			}
			isErrorHandled = true;
		}
	} else if (data) {
		isSpaceNotFound = fg('confluence_frontend_spacerestrictioncheckquery')
			? false
			: experimentalData?.experimentalSpace?.statusCode === 404 ||
				experimentalData?.experimentalSpace?.statusCode === 400;
		isSpaceRestricted = fg('confluence_frontend_spacerestrictioncheckquery')
			? false
			: experimentalData?.experimentalSpace?.statusCode === 403;
	}

	return children({
		error: isErrorHandled ? undefined : error,
		isSpaceNotFound,
		isSpaceRestricted,
		loading,
	});
}

export function isSpaceNotFoundError(error: ApolloError) {
	return isSpaceError(error, ({ statusCode }) => statusCode === 404 || statusCode === 400);
}

export function isSpaceRestrictedError(error: ApolloError) {
	return isSpaceError(error, () => isUnauthorizedError(error));
}

function isSpaceError(
	{ graphQLErrors }: ApolloError,
	extensionsPredicate: (extensions: {
		data?: { authorized?: boolean };
		statusCode?: number;
	}) => boolean,
) {
	if (graphQLErrors && graphQLErrors.length > 0) {
		for (const { extensions, path } of graphQLErrors) {
			if (
				Array.isArray(path) &&
				path.indexOf('space') !== -1 &&
				extensions &&
				extensionsPredicate(extensions)
			) {
				return true;
			}
		}
	}
	return false;
}
